import { memo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { BarChart } from 'lucide-react';

const METRICS_BY_TASK: Record<string, string[]> = {
    classification: ['Accuracy', 'Precision', 'Recall', 'F1 Score', 'Confusion Matrix'],
    regression: ['MAE', 'RMSE', 'R² (R-squared)', 'MSE'],
    anomaly: ['Anomaly Score Mean', 'Predicted Anomaly Count'],
};

export const EvaluateModelConfig = memo(({ data, onChange, inputColumns = [] }: any) => {
    const cols = (Array.isArray(inputColumns) ? inputColumns : []).map((c: any) => (typeof c === 'string' ? c : c?.name || c)).filter(Boolean);
    const taskType = data.taskType || data.modelTaskType || 'classification';
    const predictedCol = data.predictedColumn || data.scoreColumn;
    const hasScore = cols.includes('Score');
    const hasPredictedLabel = cols.includes('PredictedLabel');
    const metrics = METRICS_BY_TASK[taskType] || METRICS_BY_TASK.classification;

    return (
        <div className="space-y-6">
            <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg text-xs text-indigo-800">
                <h4 className="font-semibold mb-1 flex items-center gap-1"><BarChart className="h-3 w-3" /> Evaluate Model</h4>
                <p>Compare predictions vs ground truth. Metrics depend on task type.</p>
            </div>

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Task Type</Label>
                <Select value={taskType} onValueChange={(v) => { onChange('taskType', v); onChange('modelTaskType', v); }}>
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="Select task..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                        <SelectItem value="classification">Classification</SelectItem>
                        <SelectItem value="regression">Regression</SelectItem>
                        <SelectItem value="anomaly">Anomaly</SelectItem>
                    </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-500">Metrics: {metrics.join(', ')}</p>
            </div>

            {taskType !== 'anomaly' && (
            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Actual Column (Ground Truth)</Label>
                <Select value={data.actualColumn || data.labelColumn || '_none'} onValueChange={(v) => { const val = v === '_none' ? '' : v; onChange('actualColumn', val); onChange('labelColumn', val); }}>
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="Select actual..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                        <SelectItem value="_none">Select actual...</SelectItem>
                        {cols.map((c: string) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            )}

            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Predicted Column</Label>
                <Select value={predictedCol || '_none'} onValueChange={(v) => { const val = v === '_none' ? '' : v; onChange('predictedColumn', val); onChange('scoreColumn', val); }}>
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="Select predicted..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                        <SelectItem value="_none">Select predicted...</SelectItem>
                        {hasScore && <SelectItem value="Score">Score (predictions from Score node)</SelectItem>}
                        {hasPredictedLabel && <SelectItem value="PredictedLabel">PredictedLabel (from Score node)</SelectItem>}
                        {cols.filter((c: string) => c !== 'Score' && c !== 'PredictedLabel').map((c: string) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-500">
                    Use <strong>Score</strong> or <strong>PredictedLabel</strong> from the Score node—not input features like PhysicianName.
                </p>
            </div>

            {taskType === 'anomaly' && (
            <div className="space-y-3">
                <Label className="text-xs font-semibold text-gray-700 uppercase">Anomaly Score Column (Optional)</Label>
                <Select value={data.anomalyScoreColumn || '_none'} onValueChange={(v) => onChange('anomalyScoreColumn', v === '_none' ? '' : v)}>
                    <SelectTrigger className="w-full text-sm">
                        <SelectValue placeholder="For Anomaly Score Mean metric..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-gray-200 shadow-lg z-[100]">
                        <SelectItem value="_none">(None - use predicted column)</SelectItem>
                        {cols.map((c: string) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-[10px] text-gray-500">Raw anomaly score column from Score node for Anomaly Score Mean metric.</p>
            </div>
            )}

            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <Checkbox
                        id="appendEvaluated"
                        checked={!!data.appendEvaluatedColumn}
                        onCheckedChange={(v) => onChange('appendEvaluatedColumn', !!v)}
                    />
                    <Label htmlFor="appendEvaluated" className="text-xs font-semibold text-gray-700 cursor-pointer">
                        Append &quot;Evaluated&quot; column to output data
                    </Label>
                </div>
                <p className="text-[10px] text-gray-500">
                    When enabled, outputs the full dataset with a new column (regression: Actual − Predicted; classification: 1=match, 0=mismatch).
                </p>
                {data.appendEvaluatedColumn && (
                    <div>
                        <Label className="text-xs font-semibold text-gray-700 uppercase">Column name</Label>
                        <Input
                            className="mt-1 text-sm"
                            value={data.outputEvaluatedColumn || 'Evaluated'}
                            onChange={(e) => onChange('outputEvaluatedColumn', e.target.value || 'Evaluated')}
                            placeholder="Evaluated"
                        />
                    </div>
                )}
            </div>
        </div>
    );
});
EvaluateModelConfig.displayName = 'EvaluateModelConfig';
